/**
 * Copyright (c) 2021, Trend Micro, Inc.
 * All rights reserved.
 */
import { isSSOV2Enabled } from './web-app-shell';

import { isSSOv2UserLoggedIn, publicRoutes } from './services/helpers';
import { isGovCloudRegion } from './util';

const isWorkloadSignedIn = () =>
  window.sessionStorage.getItem('isWorkloadSignedIn') === 'true' ||
  window.sessionStorage.getItem('rID');

const getSignInPreference = () =>
  window.localStorage.getItem('C1C_SIGN_IN_METHOD')
    ? JSON.parse(window.localStorage.getItem('C1C_SIGN_IN_METHOD'))
    : false;

const getServicesList = () =>
  sessionStorage.getItem('C1C_Shell_State')
    ? JSON.parse(sessionStorage.getItem('C1C_Shell_State')).c1Services.c1ServicesList
    : null;

const getWorkloadLicenseMode = (licenseType = '') =>
  sessionStorage.getItem('C1C_Shell_State')
    ? window.location.pathname.startsWith('/workload') &&
      JSON.parse(sessionStorage.getItem('C1C_Shell_State'))?.c1Accounts?.licenseType === licenseType
    : false;

const loadRoute = (key, pathsArr) => pathsArr.find((route) => route === key);
const isUserRescue = localStorage.getItem('WS_RESCUE_LOGGED_IN_USER') === 'true';

export function prefix(location, ...prefixes) {
  return prefixes.some((prefix) => location.href.indexOf(`${location.origin}/${prefix}`) !== -1);
}

export function getParentRoute(pathname) {
  return pathname.split('/').slice(1, 2).join('/');
}

export function unAuthenticatedRoutes(location) {
  if (isSSOV2Enabled()) {
    // We want to check for parent routes only
    const currentRoute = getParentRoute(location.pathname);
    if (loadRoute(currentRoute, publicRoutes)) {
      return true;
    }
    return false;
  }
  return false;
}

export function authenticatedRoutes(location, routes) {
  if (isSSOV2Enabled()) {
    // We want to check for parent routes only
    const currentRoute = getParentRoute(location.pathname);

    if (isSSOv2UserLoggedIn()) {
      if (currentRoute === '') {
        return window.history.pushState({}, '', '/home');
      }
      return loadRoute(currentRoute, routes);
    }
  }
  return false;
}

export function appAuth(location) {
  return (
    location.pathname === '/' ||
    unAuthenticatedRoutes(location) ||
    // navigation on /support activates the authApp in GovCloud region
    (isGovCloudRegion && prefix(location, 'support')) ||
    authenticatedRoutes(location, ['admin', 'create-account', 'account-selection', 'onboarding'])
  );
}

export function navbar() {
  return true;
}

export function appLD(location) {
  const isAuthenticated = isWorkloadSignedIn() || isSSOv2UserLoggedIn();

  const shellAccountsPayload = JSON.parse(window.sessionStorage.getItem('C1C_Shell_State'))
    ?.c1Accounts;

  const accountsList = shellAccountsPayload?.c1AccountsList || [];
  const accountID = shellAccountsPayload?.c1AccountID || '';
  const currentAccountRegion = accountsList.find(({ id }) => id === accountID)?.region;

  // Batch 1
  // const batchRegions = ['us-1', 'trend-us-1', gb-1', 'de-1', 'ca-1'];
  // Batch 2
  // const batchRegions = ['in-1', 'jp-1', 'sg-1', 'au-1'];

  // const workloadMaintenanceRegions = (region) =>
  //   batchRegions.some((affectedRegion) => affectedRegion === region);

  const servicesList = getServicesList();

  const filteredServiceList = isSSOV2Enabled()
    ? servicesList && servicesList.find((service) => location.pathname.startsWith(`/${service.id}`))
    : servicesList &&
      servicesList.find(
        (service) => location.pathname.startsWith(`/${service.id}`) && service.id !== 'workload'
      );

  const validService = isAuthenticated && filteredServiceList;

  const { license, expired, id } = validService || {};

  const serviceInMaintenanceMode = false;
  // TODO Enable April 22nd, 2023 https://jr.trendmicro.com/browse/C1C-12646
  // id === 'workload' && DEPLOYMENT_TARGET === 'prod' && workloadMaintenanceRegions(currentAccountRegion);

  if (serviceInMaintenanceMode) {
    localStorage.setItem('Maintenance_Mode', 'true');
  }

  if (window.location.pathname.includes('BuyNowModal') || window.location.pathname === '/billing') {
    // [TEMP] We want to give all customers a chance to try the service out for free without needing to request a trial.
    // but entry point to Service is already available from drop-down & URL
    return false;
  } else if (serviceInMaintenanceMode) {
    return true;
  } else if (license === 'unlicensed' || (license === 'trial' && expired === true)) {
    return true;
  } else if (isAuthenticated && (window.location.pathname === '/update-account' || isUserRescue)) {
    // We want to load the landing page if the user is in login rescue mode to prevent routing to other apps.
    return true;
  } else if (
    isAuthenticated &&
    (getWorkloadLicenseMode('azure_saas_metered_billing') ||
      getWorkloadLicenseMode('utility_prepaid'))
  ) {
    return true;
  } else if (isAuthenticated && window.location.pathname === '/snyk' && license === undefined) {
    // We need to reach the LD app when snyk is not returned in the services API.
    return true;
  } else {
    return isAuthenticated && window.location.pathname.startsWith('/home');
  }
}

export function appNP(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'network');
}

export function appSE(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'application');
}

export function appFSS(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'filestorage');
}

export function appCS(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'container');
}

export function appConformity(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'conformity');
}

export function appSnyk(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'snyk');
}

export function appMC(location) {
  return (
    prefix(location, 'administration', 'integrations', 'my-user') &&
    authenticatedRoutes(location, ['administration', 'integrations', 'my-user'])
  );
}

export function appAudit(location) {
  return prefix(location, 'audit') && (isSSOv2UserLoggedIn() || isWorkloadSignedIn());
}

export function appWorkload(location) {
  const licenseCheck = appLD(location);

  return (
    !isUserRescue &&
    ((licenseCheck === false && prefix(location, 'workload')) ||
      location.pathname.startsWith('/com.trendmicro.ds.hostedservice'))
  );
}

export function appMFE(location) {
  return prefix(location, 'mfe-template');
}

export function appSupport(location) {
  // do not activate the support MFE if deployed in GovCloud
  return prefix(location, 'support') && !isGovCloudRegion;
}

export function appReporting(location) {
  return prefix(location, 'reporting') && (isSSOv2UserLoggedIn() || isWorkloadSignedIn());
}

export function appAssessment(location) {
  return prefix(location, 'assessments') && authenticatedRoutes(location, ['assessments']);
}

export function appSubscription(location) {
  return prefix(location, 'billing') && (isSSOv2UserLoggedIn() || isWorkloadSignedIn());
}

export function appTemplateScanner(location) {
  return prefix(location, 'template') && authenticatedRoutes(location, ['template']);
}

export function appC1nx(location) {
  return (
    prefix(location, 'cloud-one-central') && authenticatedRoutes(location, ['cloud-one-central'])
  );
}
